import React, { useState, useEffect } from "react";

const TermsOfService = () => {
  const [activeSection, setActiveSection] = useState("");
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showHeaderNotice, setShowHeaderNotice] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
      setShowHeaderNotice(window.scrollY < 100);

      // Update active section based on scroll position
      const sections = document.querySelectorAll("section[id]");
      const scrollPosition = window.scrollY + 100;

      sections.forEach((section) => {
        const top = (section as HTMLElement).offsetTop;
        const height = (section as HTMLElement).offsetHeight;

        if (scrollPosition >= top && scrollPosition < top + height) {
          setActiveSection(section.id);
        }
      });
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const tableOfContents = [
    { id: "parts", title: "Parts of this Agreement" },
    { id: "acceptance", title: "Acceptance of the Agreement" },
    { id: "description", title: "Description of Service" },
    { id: "beta", title: "Beta Service" },
    { id: "trial", title: "Free Trial" },
    { id: "signup", title: "User Sign up Obligations" },
    { id: "restrictions", title: "Restrictions on Use" },
    { id: "spamming", title: "Spamming and Illegal Activities" },
    { id: "third-party", title: "Third Party Applications" },
    { id: "fees", title: "Fees and Payments" },
    { id: "organization", title: "Organization Accounts" },
    { id: "privacy", title: "Personal Information and Privacy" },
    { id: "communications", title: "Communications from Motsop" },
    { id: "complaints", title: "Complaints" },
    { id: "grievance", title: "Grievance Redressal" },
    { id: "inactive", title: "Inactive User Accounts Policy" },
    { id: "hosting", title: "Hosting Location" },
    { id: "data-ownership", title: "Data Ownership" },
    { id: "user-content", title: "User Generated Content" },
    { id: "sample-files", title: "Sample Files and Applications" },
    { id: "trademark", title: "Trademark" },
    { id: "warranties", title: "Disclaimer of Warranties" },
    { id: "liability", title: "Limitation of Liability" },
    { id: "indemnification", title: "Indemnification" },
    { id: "governing-law", title: "Governing Law and Jurisdiction" },
    { id: "suspension", title: "Modification of Terms of Service" },
    { id: "modification", title: "End of Terms of Service" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white shadow-md fixed top-0 left-0 w-full z-50">
        <div className="mx-auto px-6 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 ">
              MOTSOP AI TECHNOLOGIES PRIVATE LIMITED
            </h1>
            <h2 className="text-2xl font-semibold text-gray-900 ">
              Terms of Service
            </h2>
            {showHeaderNotice && (
              <p
                className={`text-gray-600 max-w-6xl mx-auto transition-opacity duration-300 ${
                  showHeaderNotice ? "opacity-100" : "opacity-0"
                }`}
              >
                THIS IS AN AGREEMENT BETWEEN YOU OR THE ENTITY THAT YOU
                REPRESENT (HEREINAFTER “YOU” or “YOUR”) AND MOTSOP AI
                TECHNOLOGIES PRIVATE LIMITED (HEREINAFTER “MOTSOP”) GOVERNING
                YOUR USE OF MOTSOP SUITE OF ONLINE BUSINESS PRODUCTIVITY AND
                COLLABORATION SOFTWARE IF YOU ARE BASED IN INDIA. IF YOU ARE NOT
                BASED IN INDIA, OUR GLOBAL TERMS OF SERVICE WILL APPLY.
              </p>
            )}
          </div>
        </div>
      </header>
      <div className="pt-[180px] mx-auto px-6 py-8 mt-20">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Table of Contents */}
          <div className="lg:w-1/4 ">
            <div className={`fixed ${showHeaderNotice? "top-[120px]" :"top-[50px]"}  left-0 w-1/4 max-w-xs bg-white rounded-lg shadow-lg p-6 border h-[calc(100vh-100px)] overflow-y-auto z-60 mt-20`}>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                Table of Contents
              </h3>
              <nav className="space-y-1">
                {tableOfContents.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center ${
                      activeSection === item.id
                        ? "bg-blue-100 text-blue-700 font-medium"
                        : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                    }`}
                  >
                    <span className="truncate">{item.title}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:w-3/4">
            <div className="bg-white rounded-lg shadow-lg border">
              <div className="p-8 space-y-8">
                {/* Important Notice */}
                <div className="bg-amber-50 border-l-4 border-amber-400 p-6 rounded-r-lg">
                  <div className="flex items-start">
                    {/* <AlertTriangle className="w-6 h-6 text-amber-600 mr-3 flex-shrink-0 mt-0.5" /> */}
                    <div>
                      <h3 className="text-lg font-semibold text-amber-800 mb-2">
                        Important Notice
                      </h3>
                      <p className="text-amber-700">
                        This agreement applies to users based in India. If you
                        are not based in India, our Global Terms of Service will
                        apply.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Parts of Agreement */}
                <section id="parts" className="scroll-mt-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-200">
                    Parts of this Agreement
                  </h2>
                  <p className="text-gray-700 leading-relaxed">
                    This Agreement consists of the following terms and
                    conditions (hereinafter the “General Terms”) and terms and
                    conditions, if any, specific to use of individual Services
                    (hereinafter the “Service Specific Terms”). In the event of
                    a conflict between the General Terms and Service Specific
                    Terms, the Service Specific Terms shall prevail.
                  </p>
                </section>

                {/* Acceptance */}
                <section id="acceptance" className="scroll-mt-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-200">
                    Acceptance of the Agreement
                  </h2>
                  <p className="text-gray-700 leading-relaxed">
                    You must be of legal age to enter into a binding agreement
                    in order to accept the Agreement. If you do not agree to the
                    General Terms, do not use any of our Services. If you agree
                    to the General Terms and do not agree to any Service
                    Specific Terms, do not use the corresponding Service. You
                    can accept the Agreement checking a checkbox or clicking on
                    a button indicating your acceptance of the Agreement or by
                    actually using the Services.
                  </p>
                </section>

                {/* Description of Service */}
                <section id="description" className="scroll-mt-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-200">
                    Description of Service
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    We provide cloud software and applications for businesses,
                    including associated offline and mobile applications
                    ("Service" or "Services"). You may use the Services for your
                    personal and business use or for internal business purposes
                    in the organization that you represent.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    You may connect to the Services using any Internet browser
                    supported by the Services. You are responsible for obtaining
                    access to the Internet and the equipment necessary to use
                    the Services. You can create and edit content with your user
                    account and if you choose to do so, you can publish and
                    share such content.
                  </p>
                </section>

                {/* Beta Service */}
                <section id="beta" className="scroll-mt-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-200">
                    Beta Service
                  </h2>
                  <p className="text-gray-700 leading-relaxed">
                    We may offer certain Services as closed or open beta
                    services ("Beta Service" or “Beta Services”) for the purpose
                    of testing and evaluation. You agree that we have the sole
                    authority and discretion to determine the period of time for
                    testing and evaluation of Beta Services. We will be the sole
                    judge of the success of such testing and the decision, if
                    any, to offer the Beta Services as commercial services. You
                    will be under no obligation to acquire a subscription to use
                    any paid Service as a result of your subscription to any
                    Beta Service. We reserve the right to fully or partially
                    discontinue, at any time and from time to time, temporarily
                    or permanently, any of the Beta Services with or without
                    notice to you. You agree that Motsop will not be liable to
                    you or to any third party for any harm related to, arising
                    out of, or caused by the modification, suspension, or
                    discontinuance of any of the Beta Services for any reason.
                  </p>
                </section>

                {/* Free Trial */}
                <section id="trial" className="scroll-mt-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-200">
                    Free Trial
                  </h2>
                  <p className="text-gray-700 leading-relaxed">
                    If you register for a free trial of one or more Services,
                    Motsop will make the applicable Services available to you on
                    a trial basis free of charge until the earlier of (i) the
                    end of the free trial period of the applicable Services
                    (unless terminated earlier by you), (ii) the start date of
                    the paid subscription period for the applicable Services, or
                    (iii) termination by Motsop in its sole discretion. Any data
                    that you enter into the Services, and any customizations
                    made to the Services during the free trial will be
                    permanently lost unless you (i) purchase the corresponding
                    paid subscription plan for the account, (ii) purchase
                    applicable Service upgrades, or (iii) export such data
                    before the end of the trial period. Notwithstanding anything
                    contained in this Section, Services are offered as-is during
                    the free trial, without any warranty, covenant, support or
                    liability whatsoever, to the extent permitted by law.
                  </p>
                </section>

                {/* User Sign up Obligations */}
                <section id="signup" className="scroll-mt-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-200">
                    User Sign up Obligations
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    You need to sign up for a user account by providing all
                    required information in order to access or use the Services.
                    If you represent an organization and wish to use the
                    Services for corporate internal use, we recommend that you,
                    and all other users from your organization, sign up for user
                    accounts by providing your corporate contact information. In
                    particular, we recommend that you use your corporate email
                    address.
                  </p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">
                      You agree to:
                    </h4>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start">
                        <span className="w-2 h-2 bg-blue-600 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                        Provide true, accurate, current and complete information
                        about yourself as prompted by the sign-up process
                      </li>
                      <li className="flex items-start">
                        <span className="w-2 h-2 bg-blue-600 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                        Maintain and promptly update the information provided
                        during sign-up to keep it true, accurate, current, and
                        complete
                      </li>
                    </ul>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      If you provide any information that is untrue, inaccurate,
                      outdated, or incomplete, or if Motsop has reasonable
                      grounds to suspect that such information is untrue,
                      inaccurate, outdated, or incomplete, Motsop may terminate
                      your user account and refuse current or future use of any
                      or all of the Services.
                    </p>
                  </div>
                </section>

                {/* Restrictions on Use */}
                <section id="restrictions" className="scroll-mt-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-200">
                    Restrictions on Use
                  </h2>
                  <div className=" gap-4">
                    {/* Technical Restrictions */}
                    <div className="">
                      <ul className="text-sm text-gray-700 space-y-4 list-disc list-inside">
                        <li>
                          transfer the Services or otherwise make them available
                          to any third party.
                        </li>
                        <li>
                          provide any service based on the Services without
                          prior written permission from Motsop AI Technologies
                          Private Limited.
                        </li>
                        <li>
                          {" "}
                          allow user licenses to be shared or used by more than
                          one individual, except by reassigning the user license
                          to a new user.
                        </li>
                        <li>
                          except as permitted under applicable law, attempt to
                          disassemble, reverse engineer, or decompile the
                          Services.
                        </li>
                        <li>
                          use third-party links to sites without agreeing to
                          their website terms & conditions.
                        </li>
                        <li>
                          post links to third-party sites or use their logo,
                          company name, etc., without their prior written
                          permission.
                        </li>
                        <li>
                          attempt to gain unauthorized access to the Services or
                          related systems or network.
                        </li>
                        <li>
                          {" "}
                          use the Services in any manner that could damage,
                          disable, overburden, impair, or harm any server,
                          network, computer system, or resource of Motsop AI
                          Technologies Private Limited
                        </li>
                        <li>
                          use the Services to send or store material containing
                          software viruses, worms, or other harmful computer
                          codes, files, scripts, or programs
                        </li>
                        <li>
                          use the Services in any manner that interferes with or
                          disrupts the integrity, security, or performance of
                          the Services, its components, and the data contained
                          therein
                        </li>
                        <li>
                          host, display, upload, modify, publish, transmit,
                          store, update, or share any information that belongs
                          to another person or entity and to which you do not
                          have any right, including personal or confidential
                          information of any person or entity, without their
                          consent or permission.
                        </li>
                        <li>
                          violate any applicable local, state, national, or
                          international law
                        </li>
                        <li>
                          use the Services for any form of competitive or
                          benchmarking purposes.
                        </li>
                        <li>
                          remove or obscure any proprietary or other notices
                          contained in the Services.
                        </li>
                        <li>
                          use the Services in any manner that threatens the
                          unity, integrity, defence, security, or sovereignty of
                          India, friendly relations of India with other
                          countries, or public order, or causes incitement to
                          the commission of any cognizable offence or prevents
                          investigation of any offence or is insulting to other
                          countries
                        </li>
                        <li>
                          create a false identity to mislead any person as to
                          the identity or origin of any communication
                        </li>
                        <li>
                          use the Services for transmitting information that is
                          patently false and untrue, and is written or published
                          in any form, with the intent to mislead or harass a
                          person, entity, or agency for financial gain or to
                          cause injury.
                        </li>
                        <li>
                          {" "}
                          use the Services in a manner that relates to or
                          encourages any activity prohibited by law in India
                        </li>
                      </ul>
                    </div>
                  </div>
                </section>

                {/* Spamming and Illegal Activities */}
                <section id="spamming" className="scroll-mt-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-200">
                    Spamming and Illegal Activities
                  </h2>
                  <p className="text-gray-700 leading-relaxed">
                    You agree to be solely responsible for the contents of your
                    transmissions through the Services. You agree not to use the
                    Services for illegal purposes or for the transmission of
                    material that is unlawful, defamatory, insulting, harassing,
                    libelous, invasive of another's privacy (including bodily
                    privacy), abusive, threatening, harmful, vulgar,
                    pornographic, paedophilic, harmful to children, obscene,
                    racially or ethnically objectionable, or is otherwise
                    objectionable, offends religious sentiments, promotes
                    racism, contains viruses or malicious code, or that which
                    infringes or may infringe intellectual property or other
                    rights of another. You agree not to use the Services for the
                    transmission of "junk mail", "spam", "chain letters",
                    “phishing” or unsolicited mass distribution of email. Motsop
                    AI Technologies Private Limited reserves the right to
                    terminate your access to the Services if there are
                    reasonable grounds to believe that you have used the
                    Services for any illegal or unauthorized activity.
                  </p>
                </section>

                {/* Third Party Applications */}
                <section id="third-party" className="scroll-mt-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-200">
                    Third Party Applications
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    The Services of Motsop AI Technologies Private Limited may
                    integrate with various third-party applications (hereinafter
                    "Third Party Application(s)"). Access to and use of these
                    Third Party Applications may require your acceptance of
                    their respective terms of service and privacy policies
                    (hereinafter "Third Party Terms"). You are solely
                    responsible for reading and understanding the Third Party
                    Terms before accessing or using any Third Party Application.
                    You acknowledge and agree that Motsop AI Technologies
                    Private Limited is not liable for any Third Party
                    Applications. While Motsop will make efforts to provide you
                    advance notice where reasonably possible, you acknowledge
                    and agree that Motsop AI Technologies Private Limited may,
                    at any time, in its sole discretion and without notice,
                    suspend, restrict, disable access to, or remove any Third
                    Party Application from its Services without any liability to
                    you, including without limitation for any loss of profits,
                    revenue, data, goodwill, or other intangible losses.
                  </p>
                  {/* <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg">
                    <p className="text-yellow-800">
                      <strong>Important:</strong> You are solely responsible for
                      reading and understanding third-party terms before using
                      any third-party applications. Motsop is not liable for any
                      third-party applications.
                    </p>
                  </div> */}
                </section>

                {/* Fees and Payments */}
                <section id="fees" className="scroll-mt-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-200">
                    Fees and Payments
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    The Services are available under various subscription plans.
                    Payments for subscription plans of less than a year’s
                    duration may be made only by Credit Card. Your subscription
                    will be automatically renewed at the end of each
                    subscription period unless you downgrade to a free plan or
                    notify us in advance that you do not wish to renew. At
                    renewal, the subscription fee will be charged to the Credit
                    Card last used by you. You may update your card details if
                    you wish to use a different Credit Card. If you do not
                    downgrade or notify us at least seven days prior to the
                    renewal date, it shall be presumed that you have authorized
                    Motsop AI Technologies Private Limited to charge the
                    applicable fee to the last used Credit Card. Please refer to
                    our Refund Policy for further details.
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    From time to time, we may revise the pricing of any Service
                    or introduce charges for Services currently offered for
                    free. Any revised fees will not apply until the current
                    billing cycle ends. You will not be charged for any Service
                    unless you have opted for a paid plan.
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    If any applicable tax such as GST, VAT, or sales tax becomes
                    chargeable in relation to your subscription to our Services
                    ("Taxes"), Motsop AI Technologies Private Limited will
                    invoice you for the same. You agree to pay such Taxes in
                    addition to your subscription fees. A tax-compliant invoice
                    will be provided in accordance with applicable law, enabling
                    you to avail of input tax credits where eligible.
                  </p>
                </section>

                {/* Organization Accounts */}
                <section id="organization" className="scroll-mt-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-200">
                    Organization Accounts and Administrators
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    When creating an account for your organization, you may
                    designate one or more administrators. These administrators
                    will have full authority to configure the Services to suit
                    your requirements and manage end users on the organization’s
                    account. If the organization account is created by a third
                    party on your behalf, they may assume the administrator
                    role. It is your responsibility to have a suitable agreement
                    with such third parties defining their roles and limitations
                    as administrators.
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    You are responsible for: (i) keeping your organization
                    account password secure, (ii) appointing capable
                    administrators to manage the organization account, and (iii)
                    ensuring that all usage complies with this Agreement. Motsop
                    AI Technologies Private Limited is not responsible for
                    internal account administration or Service management on
                    your behalf.
                  </p>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    It is your responsibility to prevent loss of control over
                    administrator accounts. You may specify a recovery process
                    for administrator access by emailing{" "}
                    <a
                      href="mailto:legal@Motsop.com"
                      className="text-blue-600 underline hover:text-blue-800"
                    >
                      legal@Motsop.com
                    </a>
                    . provided the procedure is acceptable to Motsop. In the
                    absence of such a process, Motsop AI Technologies Private
                    Limited may, at its sole discretion, grant administrative
                    control to an individual providing sufficient proof of
                    authority to act on the organization's behalf. You agree not
                    to hold Motsop liable for any such good faith action.
                  </p>
                </section>

                {/* Privacy */}
                <section id="privacy" className="scroll-mt-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-200">
                    Personal Information and Privacy
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Personal information you provide through the use of Motsop
                    AI Technologies Private Limited's Services will be governed
                    by our Privacy Policy. By using the Services, you agree to
                    the terms of the Privacy Policy. You are responsible for
                    maintaining the confidentiality of your username, password,
                    and any sensitive information. You are also accountable for
                    all actions carried out under your user account. You agree
                    to notify us immediately of any unauthorized use of your
                    account via email to{" "}
                    <a
                      href="mailto:legal@Motsop.com"
                      className="text-blue-600 underline hover:text-blue-800"
                    >
                      accounts@Motsop.com
                    </a>
                    or by contacting us through the numbers listed on our
                    website at
                    <a
                      href="mailto:legal@Motsop.com"
                      className="text-blue-600 underline hover:text-blue-800"
                    >
                      https://www.Motsop.com/contact.
                    </a>
                    . Motsop AI Technologies Private Limited shall not be
                    responsible for any damage or loss to you or any third party
                    resulting from unauthorized access or use of your account.
                  </p>
                  {/* <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-400">
                    <p className="text-purple-800">
                      <strong>Security Reminder:</strong> You are responsible
                      for maintaining the confidentiality of your username,
                      password, and any sensitive information. Report
                      unauthorized access immediately to accounts@Motsop.com
                    </p>
                  </div> */}
                </section>

                {/* Communications */}
                <section id="communications" className="scroll-mt-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-200">
                    Communications from Motsop
                  </h2>
                  <p className="text-gray-700 leading-relaxed">
                    The Service may include certain communications from Motsop
                    AI Technologies Private Limited (“Motsop”), such as service
                    announcements, administrative messages, and newsletters. You
                    understand that these communications are considered an
                    integral part of your use of the Services. In line with our
                    commitment to safeguarding your privacy, we offer you the
                    option to opt out of receiving newsletters. However, opting
                    out of service-related announcements and administrative
                    messages is not permitted, as they are essential for the
                    operation and maintenance of the Services.
                  </p>
                </section>

                {/* Complaints */}
                <section id="complaints" className="scroll-mt-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-200">
                    Complaints
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    If we receive a complaint from any party regarding your use
                    of the Services (except for grievances handled under the
                    “Grievance Redressal” section), we will forward the
                    complaint to the primary email address associated with your
                    user account. You must respond to the complainant directly
                    within 10 days of receiving the forwarded complaint and must
                    include Motsop in your communication. If you fail to respond
                    within 10 days of our notification, we may disclose your
                    name and contact details to the complainant to facilitate
                    any legal action they may choose to take. You acknowledge
                    that your non-response within the specified period will be
                    deemed consent for Motsop to disclose your information to
                    the complainant.
                  </p>
                  {/* <div className="bg-orange-50 p-4 rounded-lg border-l-4 border-orange-400">
                    <p className="text-orange-800">
                      <strong>Response Required:</strong> You must respond to
                      the complainant directly within 10 days and include Motsop
                      in your communication. Failure to respond may result in
                      disclosure of your contact details to the complainant.
                    </p>
                  </div> */}
                </section>

                {/* Grievance Redressal */}
                <section id="grievance" className="scroll-mt-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-200">
                    Grievance Redressal
                  </h2>
                  <div className="bg-indigo-50 p-6 rounded-lg border-l-4 border-indigo-400">
                    <h4 className="font-semibold text-indigo-800 mb-3">
                      Contact Our Grievance Officer
                    </h4>
                    <p className="text-indigo-700 mb-3">
                      In accordance with the Information Technology
                      (Intermediary Guidelines and Digital Media Ethics Code)
                      Rules 2021:
                    </p>
                    <div className="flex items-center mb-2">
                      {/* <Mail className="w-5 h-5 text-indigo-600 mr-2" /> */}
                      {/* <span className="text-indigo-700">
                        grievance@Motsop.com
                      </span> */}
                      <a
                        href="mailto:legal@Motsop.com"
                        className="text-blue-600 underline hover:text-blue-800"
                      >
                        grievance@Motsop.com
                      </a>
                      .
                    </div>
                    <div className="flex items-center">
                      {/* <Phone className="w-5 h-5 text-indigo-600 mr-2" /> */}
                      <span className="text-indigo-700">
                        [Insert Phone Number]
                      </span>
                    </div>
                  </div>
                </section>

                {/* Inactive Accounts */}
                <section id="inactive" className="scroll-mt-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-200">
                    Inactive User Accounts Policy
                  </h2>
                  {/* <div className="bg-red-50 p-4 rounded-lg border-l-4 border-red-400 mb-4">
                    <p className="text-red-800 font-medium">
                      Unpaid accounts inactive for 120 continuous days will be
                      terminated and all data deleted.
                    </p>
                  </div> */}
                  <p className="text-gray-700 leading-relaxed">
                    Motsop reserves the right to terminate unpaid user accounts
                    that remain inactive for a continuous period of 120 days.
                    Upon such termination, all associated data will be deleted.
                    Prior notice will be provided along with the option to back
                    up your data. Each Service will be considered individually
                    when determining inactivity. Activity in one Service does
                    not maintain activity in another. However, in multi-user
                    accounts, activity by at least one user will retain the
                    account's active status.
                  </p>
                </section>

                {/* Hosting Location */}
                <section id="hosting" className="scroll-mt-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-200">
                    Hosting Location
                  </h2>
                  <p className="text-gray-700 leading-relaxed">
                    The location of the cloud infrastructure serving your
                    account is based on your IP address at sign-up. We may
                    migrate your account to a different facility due to changes
                    in our region-to-cloud mapping. Masking your IP during
                    sign-up is prohibited, as it determines your designated
                    region. If your actual location differs from our records,
                    Motsop may migrate your account accordingly, or
                    suspend/terminate the Service. If you are served from a
                    cloud facility outside your region and Motsop has an office
                    in your region, a local data copy may also be stored.
                  </p>
                </section>

                {/* Data Ownership */}
                <section id="data-ownership" className="scroll-mt-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-200">
                    Data Ownership
                  </h2>
                  {/* <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-400 mb-4">
                    <p className="text-green-800 font-medium">
                      You retain full ownership of any content you create or
                      store using the Services.
                    </p>
                  </div> */}
                  <p className="text-gray-700 leading-relaxed">
                    Your usage does not grant Motsop any license to use,
                    reproduce, adapt, publish, or distribute your content for
                    commercial or marketing purposes without your explicit
                    consent. However, you grant Motsop permission to access,
                    copy, store, transmit, reformat, and display your content
                    solely as required to provide the Services to you.
                  </p>
                </section>

                {/* User Generated Content */}
                <section id="user-content" className="scroll-mt-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-200">
                    User Generated Content
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    You are solely responsible for any content you transmit or
                    publish using the Services. If you publish news and current
                    affairs content, as defined under applicable Indian laws,
                    you must report your details to the Ministry of Information
                    and Broadcasting under Rule 18. Any public content may be
                    indexed by search engines. You are responsible for ensuring
                    private content remains private. You agree not to use or
                    exploit content received from others without appropriate
                    authorization. You must not remove copyright notices or
                    disable copy protection. You affirm you hold necessary
                    rights or permissions to make any content publicly
                    available. Motsop may remove or block access to content upon
                    receiving complaints regarding illegality or third-party
                    infringement. You consent to the determination of such
                    matters by a Motsop-designated agent. For procedures
                    regarding such complaints, click here.
                  </p>
                  {/* <div className="bg-amber-50 p-4 rounded-lg border-l-4 border-amber-400">
                    <p className="text-amber-800">
                      <strong>Content Responsibility:</strong> You are
                      responsible for ensuring private content remains private
                      and that you hold necessary rights for any publicly
                      available content. Motsop may remove content upon
                      receiving complaints regarding illegality or infringement.
                    </p>
                  </div> */}
                </section>

                {/* Sample Files */}
                <section id="sample-files" className="scroll-mt-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-200">
                    Sample Files and Applications
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Motsop may provide sample data, files, and applications to
                    demonstrate the utility of the Services. These are provided
                    for illustrative purposes only and consist of randomly
                    generated data. Motsop makes no guarantees as to their
                    accuracy or usefulness.
                  </p>
                </section>
                <section id="trademark" className="scroll-mt-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-200">
                    trademark
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Motsop, the Motsop logo, names of individual Services, and
                    their respective logos are trademarks of Motsop AI
                    Technologies Private Limited. You agree not to use or
                    display these trademarks without prior written permission
                    from Motsop.
                  </p>
                </section>
                <section id="warranties" className="scroll-mt-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-200">
                    Disclaimer of Warranties
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    YOU EXPRESSLY AGREE THAT YOUR USE OF THE SERVICES IS AT YOUR
                    OWN RISK. THE SERVICES ARE PROVIDED ON AN "AS IS" AND "AS
                    AVAILABLE" BASIS. MOTSOP DISCLAIMS ALL WARRANTIES, EXPRESS
                    OR IMPLIED, INCLUDING BUT NOT LIMITED TO MERCHANTABILITY AND
                    FITNESS FOR A PARTICULAR PURPOSE. MOTSOP MAKES NO GUARANTEE
                    THAT THE SERVICES WILL BE UNINTERRUPTED, SECURE, OR
                    ERROR-FREE. ANY MATERIAL DOWNLOADED OR OBTAINED THROUGH THE
                    SERVICE IS AT YOUR OWN DISCRETION, AND YOU WILL BE SOLELY
                    RESPONSIBLE FOR ANY DAMAGE TO YOUR DEVICES OR DATA RESULTING
                    FROM ITS USE. NO ORAL OR WRITTEN ADVICE FROM MOTSOP OR ITS
                    REPRESENTATIVES SHALL CREATE ANY WARRANTY NOT EXPRESSLY
                    STATED HEREIN.
                  </p>
                </section>
                <section id="liability" className="scroll-mt-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-200">
                    Limitation of Liability
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    UNDER NO CIRCUMSTANCES SHALL MOTSOP BE LIABLE FOR INDIRECT,
                    INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES,
                    INCLUDING LOSS OF BUSINESS, REVENUE, OR DATA, EVEN IF
                    ADVISED OF SUCH POSSIBILITY. IN ANY CASE, MOTSOP’S TOTAL
                    LIABILITY FOR ANY CLAIM ARISING OUT OF OR IN CONNECTION WITH
                    YOUR USE OF THE SERVICES SHALL NOT EXCEED ₹50,000 OR THE
                    TOTAL FEES PAID BY YOU FOR THE SERVICES IN THE TWELVE (12)
                    MONTHS PRIOR TO THE INCIDENT, WHICHEVER IS GREATER
                  </p>
                </section>
                <section id="liability" className="scroll-mt-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-200">
                    Indemnification
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    You agree to indemnify and hold harmless Motsop, its
                    directors, officers, employees, affiliates, and agents, from
                    any liabilities, losses, damages, or expenses (including
                    legal fees) arising out of your use of the Services, your
                    violation of any rights of a third party, or any breach of
                    law or these Terms, except when such use is authorized by
                    Motsop.
                  </p>
                </section>
                <section id="indemnification" className="scroll-mt-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-200">
                    Governing Law and Jurisdiction
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Any disputes arising under or related to these Terms shall
                    be governed by the laws of India. The courts located in
                    Chennai, Tamil Nadu shall have exclusive jurisdiction over
                    such disputes, regardless of conflict of law principles.
                  </p>
                </section>
                <section id="governing-law" className="scroll-mt-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-200">
                    Suspension and Termination
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Motsop may suspend or disable access to your account in
                    cases of suspected illegal activity, extended inactivity, or
                    requests by governmental authorities. You may object to such
                    suspension within 30 days by contacting{" "}
                    <a
                      href="mailto:legal@Motsop.com"
                      className="text-blue-600 underline hover:text-blue-800"
                    >
                      legal@Motsop.com
                    </a>
                    . We may terminate an account after 30 days of suspension.
                    You may request account termination at any time. Termination
                    will result in denial of access and deletion of your data.
                    Motsop may also terminate access due to violation of the
                    Agreement or technical discontinuation of Beta Services. You
                    may terminate the account if Motsop breaches this Agreement,
                    and receive a prorated refund for any prepaid fees.
                  </p>
                </section>
                <section id="suspension" className="scroll-mt-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-200">
                    Modification of Terms of Service
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Motsop reserves the right to amend these Terms at any time
                    with notice through email or service announcements. For
                    significant changes affecting your rights, you will receive
                    at least 30 days’ notice. If you object to such changes, you
                    may terminate the Service within the notice period and
                    receive a prorated refund. Continued use of the Service
                    post-effective date constitutes acceptance of the revised
                    Terms.
                  </p>
                </section>
                <section id="modification" className="scroll-mt-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-200">
                    End of Terms of Service
                  </h2>

                  <p className="text-gray-700 leading-relaxed mb-4">
                    For any questions or concerns regarding this Agreement,
                    please contact us at{" "}
                    <a
                      href="mailto:legal@Motsop.com"
                      className="text-blue-600 underline hover:text-blue-800"
                    >
                      legal@Motsop.com
                    </a>
                    .
                  </p>
                </section>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 z-50 bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 transition"
          aria-label="Scroll to top"
        ></button>
      )}
    </div>
  );
};

export default TermsOfService;
